import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FaFacebookF,
  FaTelegramPlane,
  FaWhatsapp,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
// Assuming these imports are correctly defined and located in your project
import { getEmergencyContacts } from "../../Api/emergencyContactsApi";
import { getSocialMediaLinks } from "../../Api/socialMediaLinkApi";
import { getContactInfos } from "../../Api/contactInfoApi";
import Logo from "../../assets/LOGO_WHITE_01.svg";

function Footer() {
  const [contacts, setContacts] = useState([]);
  const [contactInfos, setContactInfos] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    facebook: "",
    telegram: "",
    whatsapp: "",
  });

  // --- Data Fetching Logic (Production-Ready) ---

  // Fetch emergency contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await getEmergencyContacts();
        setContacts(res.data || res || []); // fallback to empty array
      } catch {
        setContacts([]); // silent fail
      }
    };
    fetchContacts();
  }, []);

  // Fetch contact infos
  useEffect(() => {
    const fetchContactInfos = async () => {
      try {
        const res = await getContactInfos();
        setContactInfos(res.data || res || []); // fallback to empty array
      } catch {
        setContactInfos([]); // silent fail
      }
    };
    fetchContactInfos();
  }, []);

  // Fetch social media links
  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await getSocialMediaLinks();
        const links = res.data || res || [];

        if (Array.isArray(links)) {
          const social = {};
          links.forEach((link) => {
            const platform = link.platform?.toLowerCase();
            if (platform === "facebook") social.facebook = link.url;
            if (platform === "telegram") social.telegram = link.url;
            if (platform === "whatsapp") social.whatsapp = link.url;
          });
          setSocialLinks(social);
        } else {
          setSocialLinks(links || {});
        }
      } catch {
        setSocialLinks({}); // silent fail
      }
    };
    fetchLinks();
  }, []);

  // --- Sitemap & Contact Data (UNCHANGED) ---
  const sitemapLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    { name: "Residences", path: "/ccd-residences" },
    { name: "Amenities", path: "/ccd-amenity" },
    { name: "Community", path: "/community" },
    { name: "Services", path: "/service" },
    { name: "Governance Updates", path: "/governance-updates" },
    { name: "Communications", path: "/communications" },
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms & Conditions", path: "/terms-conditions" },
  ];

  const staticContact = {
    location: "CCD Gated Community, Laga Tafo–Laga Dadi, Addis Ababa, Ethiopia",
    email: "ccdhomeownerstafo@gmail.com",
    phone: ["+251962525001"],
  };

  const displayLocation = contactInfos[0]?.location || staticContact.location;
  const displayEmail = contactInfos[0]?.email || staticContact.email;
  const displayPhones =
    contacts.length > 0 ? contacts.map((c) => c.phone) : staticContact.phone;

  // Split sitemap into two columns for 4-column layout
  // Note: Only slicing up to 8 links for the main link columns
  const mainSitemapLinks = sitemapLinks.slice(0, 8);
  const splitIndex = Math.ceil(mainSitemapLinks.length / 2);
  const mainLinks1 = mainSitemapLinks.slice(0, splitIndex);
  const mainLinks2 = mainSitemapLinks.slice(splitIndex);

  // --- RENDERED JSX (BALANCED & RESPONSIVE) ---
  return (
    // 💡 Applied dark mode classes: dark:bg-gray-950 for a very dark background
    <footer className="bg-Dark dark:bg-gray-950 font-Poppins text-white dark:text-gray-200 border-t border-Light/10 dark:border-white/10">
      {/* Footer Top: Main Content Grid - Balanced Columns */}
      <div
        className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 gap-x-10 gap-y-12 
                    md:grid-cols-4 md:gap-x-12 
                    lg:grid-cols-7 lg:divide-x lg:divide-white/10 dark:lg:divide-gray-700" // 💡 Dark mode divider
      >
        {/* Column 1: Logo, Mission, Socials (Takes 3/7 columns on large screens) */}
        <div className="col-span-2 md:col-span-2 lg:col-span-3 lg:pr-16">
          <NavLink to="/" className="flex items-center gap-3 mb-6">
            <img
              src={Logo}
              alt="CCD Logo"
              className="h-10 w-auto object-contain"
            />
            <h2 className="text-xl font-extrabold tracking-wider hover:text-Light transition-colors">
              CCD Gated Community
            </h2>
          </NavLink>
          {/* 💡 Adjusted text color for dark mode reading ease */}
          <p className="text-white dark:text-gray-400 text-sm leading-relaxed mb-8">
            A vibrant, secure, and well-managed gated community, blending modern
            living with comfort and safety in Addis Ababa.
          </p>

          {/* Social Links */}
          <div className="flex space-x-4 items-center">
            {socialLinks.facebook && (
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect on Facebook"
                // 💡 Applied dark mode for hover text on social icons
                className="bg-white/10 dark:bg-white/5 p-2.5 rounded-full text-white hover:bg-Light hover:text-Dark dark:hover:text-Dark transition-all duration-300 transform hover:scale-110 shadow-md"
              >
                <FaFacebookF size={18} />
              </a>
            )}
            {socialLinks.telegram && (
              <a
                href={socialLinks.telegram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Join our Telegram channel"
                // 💡 Applied dark mode for hover text on social icons
                className="bg-white/10 dark:bg-white/5 p-2.5 rounded-full text-white hover:bg-Light hover:text-Dark dark:hover:text-Dark transition-all duration-300 transform hover:scale-110 shadow-md"
              >
                <FaTelegramPlane size={18} />
              </a>
            )}
            {socialLinks.whatsapp && (
              <a
                href={`https://wa.me/${socialLinks.whatsapp.replace(
                  /\D/g,
                  ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Message us on WhatsApp"
                // 💡 Applied dark mode for hover text on social icons
                className="bg-white/10 dark:bg-white/5 p-2.5 rounded-full text-white hover:bg-Light hover:text-Dark dark:hover:text-Dark transition-all duration-300 transform hover:scale-110 shadow-md"
              >
                <FaWhatsapp size={18} />
              </a>
            )}
          </div>
        </div>

        {/* Column 2: Quick Links Part 1 (Takes 1/7 columns on large screens) */}
        <div className="md:pl-0 lg:col-span-1 lg:pl-16">
          <h3 className="text-lg font-bold uppercase mb-5 tracking-widest text-white dark:text-white">
            Links
          </h3>
          <ul className="space-y-3 text-white dark:text-gray-300 text-sm">
            {" "}
            {/* 💡 Dark mode link text */}
            {mainLinks1.map((link) => (
              <li key={link.name}>
                <NavLink
                  to={link.path}
                  className="hover:text-Light transition-colors hover:underline underline-offset-4"
                >
                  {link.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3: Quick Links Part 2 (Takes 1/7 columns on large screens) */}
        <div className="lg:pl-8">
          <h3 className="text-lg font-bold uppercase mb-5 tracking-widest text-white dark:text-white">
            Explore
          </h3>
          <ul className="space-y-3 text-white dark:text-gray-300 text-sm">
            {" "}
            {/* 💡 Dark mode link text */}
            {mainLinks2.map((link) => (
              <li key={link.name}>
                <NavLink
                  to={link.path}
                  className="hover:text-Light transition-colors hover:underline underline-offset-4"
                >
                  {link.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4: Contact Information (Takes 2/7 columns on large screens) */}
        <div className="col-span-2 md:col-span-1 lg:col-span-2 lg:pl-8">
          <h3 className="text-lg font-bold uppercase mb-5 tracking-widest text-white dark:text-white">
            Contact Us
          </h3>
          <ul className="space-y-5 text-white dark:text-gray-300 text-sm">
            {" "}
            {/* 💡 Dark mode contact text */}
            {/* Location */}
            <li className="flex items-start gap-3">
              <FaMapMarkerAlt
                className="mt-1 text-Light flex-shrink-0"
                size={16}
              />
              <span>{displayLocation}</span>
            </li>
            {/* Email */}
            <li className="flex items-start gap-3">
              <FaEnvelope className="mt-1 text-Light flex-shrink-0" size={16} />
              <a
                href={`mailto:${displayEmail}`}
                className="hover:text-Light transition-colors"
              >
                {displayEmail}
              </a>
            </li>
            {/* Phones */}
            <li className="flex items-start gap-3">
              <FaPhoneAlt className="mt-1 text-Light flex-shrink-0" size={16} />
              <div className="flex flex-col gap-1">
                <span className="text-white dark:text-white font-semibold">
                  Emergency & Office:
                </span>
                {displayPhones.map((phone, idx) => (
                  <a
                    key={idx}
                    href={`tel:${phone}`}
                    className="hover:text-Light transition-colors"
                  >
                    {phone}
                  </a>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* --- Footer Bottom: Copyright and Policy Links --- */}
      {/* 💡 Applied dark mode for border color */}
      <div className="border-t border-white/10 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row justify-between items-center text-white dark:text-gray-400 text-xs sm:text-sm">
          {" "}
          {/* 💡 Dark mode copyright text */}
          {/* Copyright */}
          <div className="order-2 sm:order-1 mt-4 sm:mt-0">
            &copy; 2025{" "}
            <span className="text-Light font-semibold">
              CCD Gated Community
            </span>
            . All rights reserved.
          </div>
          {/* Policy Links */}
          <div className="order-1 sm:order-2 flex flex-wrap justify-center gap-6">
            {sitemapLinks.slice(-2).map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                // 💡 Ensured NavLink text remains visible and hover works
                className="hover:text-Light transition-colors text-white dark:text-gray-400 hover:underline underline-offset-4"
              >
                {item.name}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
